import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import type {
  CompetencyArea,
  Competency,
} from '../../types/GrowthWheelManagement'
import '../../css/gw-wheel-management.scss'
import { WheelManagementImageCard } from './WheelManagementImageCard'
import { AddCompetencyForm } from './AddCompetencyForm'
import { WheelManagementAreaCard } from './WheelManagementAreaCard'
import { WheelManagementDeleteModal } from './WheelManagementDeleteModal'
import {
  EditSubCompetencyPanel,
  competencyEditableContentEqual,
} from './EditSubCompetencyPanel'
import { buildSubCompetencyEditWarningModal } from './wheelManagementWarningModalService'

type DeleteModalState =
  | { kind: 'competency'; areaId: string; name: string }
  | { kind: 'subCompetency'; competencyId: string; name: string }

type WarningModalState = {
  kind: 'subCompetencyEdit'
  competency: Competency
}

interface WheelManagementProps {
  areas: CompetencyArea[]
  competencies: Competency[]
  roleName?: string
  wheelImageUrl?: string | null
  onUpdateArea: (areaId: string, updates: Partial<CompetencyArea>) => void
  onDeleteArea: (areaId: string) => void
  onAddArea: (area: Omit<CompetencyArea, 'id'>) => void
  onUpdateCompetency: (
    competencyId: string,
    updates: Partial<Competency>,
  ) => void | Promise<void>
  onDeleteCompetency: (competencyId: string) => void
  onAddCompetency: (competency: Omit<Competency, 'id'>) => void
  onReorderAreas: (areas: CompetencyArea[]) => void
  onReorderCompetencies: (
    areaId: string,
    competenciesInAreaOrder: Competency[],
  ) => void
}

export function WheelManagement({
  areas,
  competencies,
  roleName,
  wheelImageUrl,
  onUpdateArea,
  onDeleteArea,
  onAddArea,
  onUpdateCompetency,
  onDeleteCompetency,
  onAddCompetency,
  onReorderAreas,
  onReorderCompetencies,
}: WheelManagementProps) {
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set())
  const [editingAreaId, setEditingAreaId] = useState<string | null>(null)
  const [editingAreaName, setEditingAreaName] = useState('')
  const [editingAreaDesc, setEditingAreaDesc] = useState('')
  const [editingAreaBaseline, setEditingAreaBaseline] = useState<{
    name: string
    description: string
  } | null>(null)

  const [showAddArea, setShowAddArea] = useState(false)
  const [newAreaName, setNewAreaName] = useState('')
  const [newAreaDesc, setNewAreaDesc] = useState('')

  const [addingToAreaId, setAddingToAreaId] = useState<string | null>(null)
  const [newCompetencyName, setNewCompetencyName] = useState('')
  const [newCompetencyDesc, setNewCompetencyDesc] = useState('')

  /** competency = area row; sub-competency = competency row */
  const [deleteModal, setDeleteModal] = useState<DeleteModalState | null>(null)
  const [warningModal, setWarningModal] = useState<WarningModalState | null>(null)

  const [editingCompetency, setEditingCompetency] = useState<Competency | null>(
    null,
  )
  const [editedCompetency, setEditedCompetency] = useState<Competency | null>(
    null,
  )

  const toggleArea = (areaId: string) => {
    const next = new Set(expandedAreas)
    if (next.has(areaId)) {
      next.delete(areaId)
    } else {
      next.add(areaId)
    }
    setExpandedAreas(next)
  }

  const startEditArea = (area: CompetencyArea) => {
    setEditingAreaId(area.id)
    setEditingAreaName(area.name)
    setEditingAreaDesc(area.description)
    setEditingAreaBaseline({
      name: area.name,
      description: area.description,
    })
  }

  const saveAreaEdit = () => {
    if (
      !editingAreaId ||
      !editingAreaBaseline ||
      !editingAreaName.trim() ||
      (editingAreaName === editingAreaBaseline.name &&
        editingAreaDesc === editingAreaBaseline.description)
    ) {
      return
    }
    onUpdateArea(editingAreaId, {
      name: editingAreaName,
      description: editingAreaDesc,
    })
    setEditingAreaId(null)
    setEditingAreaName('')
    setEditingAreaDesc('')
    setEditingAreaBaseline(null)
  }

  const cancelAreaEdit = () => {
    setEditingAreaId(null)
    setEditingAreaName('')
    setEditingAreaDesc('')
    setEditingAreaBaseline(null)
  }

  const handleAddArea = () => {
    if (newAreaName.trim()) {
      onAddArea({
        name: newAreaName.trim(),
        description: newAreaDesc.trim(),
      })
      setNewAreaName('')
      setNewAreaDesc('')
      setShowAddArea(false)
    }
  }

  const handleDeleteArea = (areaId: string) => {
    onDeleteArea(areaId)
    setDeleteModal(null)
  }

  const handleAddCompetency = () => {
    if (addingToAreaId && newCompetencyName.trim()) {
      onAddCompetency({
        name: newCompetencyName.trim(),
        area: addingToAreaId,
        description: newCompetencyDesc.trim(),
        levelDescriptions: { 1: '', 2: '', 3: '', 4: '', 5: '' },
      })
      setNewCompetencyName('')
      setNewCompetencyDesc('')
      setAddingToAreaId(null)
    }
  }

  const handleDeleteCompetency = (competencyId: string) => {
    onDeleteCompetency(competencyId)
    setDeleteModal(null)
  }

  const startEditCompetency = (competency: Competency) => {
    setEditingCompetency(competency)
    setEditedCompetency({ ...competency })
  }

  const saveCompetencyEdit = () => {
    if (
      !editedCompetency ||
      !editingCompetency ||
      !editedCompetency.name.trim() ||
      competencyEditableContentEqual(editedCompetency, editingCompetency)
    ) {
      return
    }
    setWarningModal({
      kind: 'subCompetencyEdit',
      competency: { ...editedCompetency },
    })
  }

  const confirmWarningModal = async () => {
    if (!warningModal) return

    if (warningModal.kind === 'subCompetencyEdit') {
      await onUpdateCompetency(
        warningModal.competency.id,
        warningModal.competency,
      )
      setEditingCompetency(null)
      setEditedCompetency(null)
      setWarningModal(null)
    }
  }

  const getCompetenciesForArea = (areaId: string) =>
    (competencies || []).filter(comp => comp.area === areaId)

  const moveArea = (dragIndex: number, hoverIndex: number) => {
    const newAreas = [...areas]
    const [draggedItem] = newAreas.splice(dragIndex, 1)
    newAreas.splice(hoverIndex, 0, draggedItem)
    onReorderAreas(newAreas)
  }

  const moveCompetency = (
    competencyId: string,
    direction: 'up' | 'down',
    areaId: string,
  ) => {
    const areaCompetencies = getCompetenciesForArea(areaId)
    const index = areaCompetencies.findIndex(c => c.id === competencyId)
    if (
      (direction === 'up' && index <= 0) ||
      (direction === 'down' && index >= areaCompetencies.length - 1)
    ) {
      return
    }
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const reordered = [...areaCompetencies]
    ;[reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ]
    onReorderCompetencies(areaId, reordered)
  }

  const closeEditPanel = () => {
    setEditingCompetency(null)
    setEditedCompetency(null)
  }

  const warningContent =
    warningModal?.kind === 'subCompetencyEdit'
      ? buildSubCompetencyEditWarningModal(warningModal.competency.name)
      : null

  return (
    <div className="gw-wm">
      <div>
        <h1 className="gw-wm__page-title">Growth Wheel Management</h1>
        <p className="gw-wm__lead">
          Configure the competency framework structure and content for your
          organization.
        </p>
      </div>

      <WheelManagementImageCard
        wheelImageUrl={wheelImageUrl}
        roleName={roleName}
      />

      <div className="gw-wm__card">
        <div className="gw-wm__framework-head">
          <div>
            <h2 className="gw-wm__card-title" style={{ marginBottom: 0 }}>
              Competencies
            </h2>
          </div>
          <button
            type="button"
            className="gw-wm__btn-primary"
            onClick={() => setShowAddArea(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Competency
          </button>
        </div>

        {areas.length === 0 && !showAddArea && (
          <div className="gw-wm__empty">
            <div className="gw-wm__empty-icon">
              <FontAwesomeIcon icon={faPlus} />
            </div>
            <p className="mb-3">
              No competencies yet. Add a competency to get started.
            </p>
          </div>
        )}

        {showAddArea && (
          <AddCompetencyForm
            newAreaName={newAreaName}
            newAreaDesc={newAreaDesc}
            onChangeName={setNewAreaName}
            onChangeDesc={setNewAreaDesc}
            onSubmit={handleAddArea}
            onCancel={() => {
              setShowAddArea(false)
              setNewAreaName('')
              setNewAreaDesc('')
            }}
          />
        )}

        <div className="gw-wm__area-list">
          {areas.map((area, index) => {
            const areaCompetencies = getCompetenciesForArea(area.id)
            const isExpanded = expandedAreas.has(area.id)
            const isEditing = editingAreaId === area.id
            const areaEditSaveEnabled =
              isEditing &&
              editingAreaBaseline !== null &&
              Boolean(editingAreaName.trim()) &&
              (editingAreaName !== editingAreaBaseline.name ||
                editingAreaDesc !== editingAreaBaseline.description)

            return (
              <WheelManagementAreaCard
                key={area.id}
                area={area}
                index={index}
                areasLength={areas.length}
                areaCompetencies={areaCompetencies}
                isExpanded={isExpanded}
                isEditing={isEditing}
                areaEditSaveEnabled={areaEditSaveEnabled}
                editingAreaName={editingAreaName}
                editingAreaDesc={editingAreaDesc}
                onEditingNameChange={setEditingAreaName}
                onEditingDescChange={setEditingAreaDesc}
                onToggleExpand={() => toggleArea(area.id)}
                onStartEdit={() => startEditArea(area)}
                onSaveEdit={saveAreaEdit}
                onCancelEdit={cancelAreaEdit}
                onMoveAreaUp={() => moveArea(index, index - 1)}
                onMoveAreaDown={() => moveArea(index, index + 1)}
                onDeleteAreaClick={() =>
                  setDeleteModal({
                    kind: 'competency',
                    areaId: area.id,
                    name: area.name,
                  })
                }
                addingToAreaId={addingToAreaId}
                newCompetencyName={newCompetencyName}
                newCompetencyDesc={newCompetencyDesc}
                onNewCompetencyNameChange={setNewCompetencyName}
                onNewCompetencyDescChange={setNewCompetencyDesc}
                onStartAddCompetency={() => setAddingToAreaId(area.id)}
                onCancelAddCompetency={() => {
                  setAddingToAreaId(null)
                  setNewCompetencyName('')
                  setNewCompetencyDesc('')
                }}
                onSubmitNewCompetency={handleAddCompetency}
                onMoveCompetency={(competencyId, direction) =>
                  moveCompetency(competencyId, direction, area.id)
                }
                onEditCompetency={startEditCompetency}
                onDeleteCompetencyClick={(competencyId, name) =>
                  setDeleteModal({
                    kind: 'subCompetency',
                    competencyId,
                    name,
                  })
                }
              />
            )
          })}
        </div>
      </div>

      {deleteModal && (
        <WheelManagementDeleteModal
          title={
            deleteModal.kind === 'competency'
              ? 'Delete competency'
              : 'Delete sub-competency'
          }
          confirmLabel={
            deleteModal.kind === 'competency'
              ? 'Delete competency'
              : 'Delete sub-competency'
          }
          onCancel={() => setDeleteModal(null)}
          onConfirm={() =>
            deleteModal.kind === 'competency'
              ? handleDeleteArea(deleteModal.areaId)
              : handleDeleteCompetency(deleteModal.competencyId)
          }
        >
          <p className="gw-wm__hint">
            {deleteModal.kind === 'competency' ? (
              <>
                Are you sure you want to delete &quot;{deleteModal.name}&quot;?
                This will also delete all{' '}
                {getCompetenciesForArea(deleteModal.areaId).length}{' '}
                sub-competencies, their levels 1–5 rubric definitions, and any
                associated assessment scores and notes.
              </>
            ) : (
              <>
                Are you sure you want to delete &quot;{deleteModal.name}&quot;?
                This will also delete all levels 1–5 rubric definitions for this
                sub-competency and remove all associated assessment scores and
                notes.
              </>
            )}
          </p>
        </WheelManagementDeleteModal>
      )}

      {editingCompetency && editedCompetency && (
        <EditSubCompetencyPanel
          editedCompetency={editedCompetency}
          baselineCompetency={editingCompetency}
          setEditedCompetency={setEditedCompetency}
          roleName={roleName}
          parentAreaName={areas.find(a => a.id === editedCompetency.area)?.name}
          onClose={closeEditPanel}
          onSave={saveCompetencyEdit}
        />
      )}

      {warningModal?.kind === 'subCompetencyEdit' && (
        <WheelManagementDeleteModal
          tone="warning"
          title={warningContent?.title ?? ''}
          confirmLabel={warningContent?.confirmLabel ?? ''}
          onCancel={() => setWarningModal(null)}
          onConfirm={confirmWarningModal}
          irreversibleMessage="This reset cannot be undone."
        >
          <p className="gw-wm__hint">{warningContent?.description}</p>
        </WheelManagementDeleteModal>
      )}
    </div>
  )
}
